/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios'
import clsx from 'clsx'
import {Megaphone, MessageSquareDot} from 'lucide-react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {useAuth} from '../../../../app/modules/auth'
import {
  getChatNotifications,
  getStatusNotifications,
} from '../../../../app/services/notification.service'
import {ClaimNotificationListener} from '../../../components/notifications/ClaimNotificationListener'
import {KTIcon, toAbsoluteUrl} from '../../../helpers'
import {createPusherClient} from '../../../helpers/pusherClient'
import useNotificationStore from '../../../store/notificationStore'
import {useLayout} from '../../core'
import {usePageData} from '../../core/PageData'
import {Header} from './Header'
import {Navbar} from './Navbar'

interface Claim {
  id: string
  memberName: string
  claimNumber: string
  cardNumber: string
  policyNumber: string
  customerGroup: string
  status: string
  claimDate: string
  claimType: string
}

const MAX_TOASTS = 3

export function HeaderWrapper() {
  const {config, classes} = useLayout()
  const {claimNumbers} = usePageData()
  const {currentUser} = useAuth()
  const providerCode = currentUser?.providerCode
  const addNotification = useNotificationStore((state) => state.addNotification)
  const notifications = useNotificationStore((state) => state.notifications)
  const unreadCount = useNotificationStore((state) => state.unreadCount)
  const resetUnreadCount = useNotificationStore((state) => state.resetUnreadCount)
  const hasViewedNotifications = useNotificationStore((state) => state.hasViewedNotifications)
  const setHasViewedNotifications = useNotificationStore((state) => state.setHasViewedNotifications)
  const navigate = useNavigate()
  const user = useAuth()
  const {auth} = useAuth()
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [activeNotificationTab, setActiveNotificationTab] = useState<'chat' | 'status'>('chat')

  // API Fetch states - Chat & Status notifications
  const [headerChatNotifications, setHeaderChatNotifications] = useState<any[]>([])
  const [headerStatusNotifications, setHeaderStatusNotifications] = useState<any[]>([])
  const [headerLoading, setHeaderLoading] = useState(false)
  const activeToasts = useRef<Set<string | number>>(new Set())

  console.log('header notification', headerChatNotifications)
  console.log('header statu notification', headerStatusNotifications)

  // Fetch header notifications from API
  const fetchHeaderNotifications = useCallback(async () => {
    setHeaderLoading(true)
    try {
      const [chatResponse, statusResponse] = await Promise.all([
        getChatNotifications({limit: 10, onlyUnread: true}),
        getStatusNotifications({limit: 10, onlyUnread: true}),
      ])

      if (chatResponse.isSuccess && chatResponse.data) {
        setHeaderChatNotifications(chatResponse.data.items)
      }
      if (statusResponse.isSuccess && statusResponse.data) {
        setHeaderStatusNotifications(statusResponse.data.items)
      }
    } catch (error) {
      console.error('Error fetching header notifications:', error)
    } finally {
      setHeaderLoading(false)
    }
  }, [providerCode])

  // Fetch notifications saat component mount (page load)
  useEffect(() => {
    if (providerCode) {
      fetchHeaderNotifications()
    }
  }, [providerCode, fetchHeaderNotifications])

  const fetchClaimDetails = async (claimNumber: string) => {
    const apiUrl = `${process.env.REACT_APP_API_PROVIDER_URL}/api/Claims/GetByClaimNumber/${claimNumber}`
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
        },
      })
      console.log('Claim details fetched:', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching claim details:', error)
      return null
    }
  }
  // Function to mark a notification as read by claimnumber and send a user updated notification
  const markNotificationAsRead = async (claimNumber: string) => {
    try {
      // const apiUrl = `https://localhost:7036/api/Provider/MarkNotificationAsRead`
      const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Provider/MarkNotificationAsRead`
      const userName = user?.currentUser?.username || 'SYSTEM'

      // Build query parameters
      const queryParams = new URLSearchParams()
      queryParams.append('claimNumber', claimNumber)
      queryParams.append('userName', userName)

      const fullUrl = `${apiUrl}?${queryParams.toString()}`

      console.log('Marking notification as read with URL:', fullUrl)

      const response = await axios.post(
        fullUrl,
        {}, // Empty body since data is in query params
        {
          headers: {
            Authorization: `Bearer ${auth?.token}`,
          },
        }
      )

      if (response.data.isSuccess) {
        console.log('Notification marked as read successfully')
        return response.data
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const handleClaimAction = async (claimNumber: string, markAsRead: boolean = false) => {
    console.log('Test Button : ', claimNumber)
    const claimDetails = await fetchClaimDetails(claimNumber)
    if (claimDetails) {
      console.log('Claim details:', claimDetails)
      if (markAsRead === true) {
        await markNotificationAsRead(claimNumber)
      }

      const path = claimDetails.claim?.treatmentType === 'Rawat inap' ? '/inpatient' : '/outpatient'
      navigate(path, {state: {claimDetails}})
    }
  }

  useEffect(() => {
    if (!providerCode) {
      console.log('[Pusher:FileChannel] ⚠️ No provider code available')
      return
    }

    console.log('[Pusher:FileChannel] 🔄 Initializing Pusher...')
    console.log('[Pusher:FileChannel] Provider Code:', providerCode)

    const pusher = createPusherClient('FileChannel')

    // Subscribe to provider channel
    const channel = pusher.subscribe(`file-channel-${providerCode}`)
    console.log('[Pusher:FileChannel] Subscribed to:', `file-channel-${providerCode}`)

    channel.bind(`get-${providerCode}`, (data: any) => {
      console.log('[Pusher:FileChannel] 📄 Received notification:', data)
      if (data && data.message) {
        const {message} = data
        const {Status, ClaimNumber, UpdatedBy, UpdatedAt} = message

        if (Status === 'INVALID' || Status === 'Request Document' || Status === 'VALID') {
          // Add notification to Zustand store with isRead set to false
          addNotification({
            type: 'file',
            ClaimNumber,
            Status,
            UpdatedBy,
            UpdatedAt: new Date(UpdatedAt).toLocaleString(),
            isRead: false, // New notifications are unread
          })

          if (activeToasts.current.size < MAX_TOASTS) {
            const toastMessage =
              Status === 'VALID'
                ? `Document Claim ${ClaimNumber} has been approved by ${UpdatedBy} on ${new Date(
                    UpdatedAt
                  ).toLocaleString()}`
                : Status === 'Request Document'
                ? `Document Claim ${ClaimNumber} has been added for review by ${UpdatedBy} on ${new Date(
                    UpdatedAt
                  ).toLocaleString()}`
                : `Document Claim ${ClaimNumber} has been rejected by ${UpdatedBy} on ${new Date(
                    UpdatedAt
                  ).toLocaleString()}`

            const toastId = toast(toastMessage, {
              position: 'top-right',
              bodyStyle: {width: '20rem'},
              type: Status === 'INVALID' ? 'error' : 'info',
              onClose: () => {
                // Remove from tracking when toast closes
                activeToasts.current.delete(toastId.toString())
              },
            })

            // Track the toast
            activeToasts.current.add(toastId.toString())
          }
        }
      }
    })

    // Cleanup function
    return () => {
      channel.unbind_all()
      pusher.unsubscribe(`file-channel-${providerCode}`)
      pusher.disconnect()
    }
  }, [providerCode])

  const handleNotificationBellClick = () => {
    setIsNotificationOpen(!isNotificationOpen)
    // Red dot disappears when bell is clicked
    resetUnreadCount()
    if (!isNotificationOpen) {
      setHasViewedNotifications(true)
      fetchHeaderNotifications()
    }
  }

  if (!config.app?.header?.display) {
    return null
  }

  return (
    <div id='kt_app_header' className='app-header'>
      {/* Claim Notification Listener for chat and status updates */}
      <ClaimNotificationListener />

      <div
        id='kt_app_header_container'
        className={clsx(
          'app-container flex-lg-grow-1',
          classes.headerContainer.join(' '),
          config.app?.header?.default?.containerClass
        )}
      >
        {config.app.sidebar?.display && (
          <>
            <div
              className='d-flex align-items-center d-lg-none ms-n2 me-2'
              title='Show sidebar menu'
            >
              <div
                className='btn btn-icon btn-active-color-primary w-35px h-35px'
                id='kt_app_sidebar_mobile_toggle'
              >
                <KTIcon iconName='abstract-14' className=' fs-1' />
              </div>
              <div className='d-flex align-items-center flex-grow-1 flex-lg-grow-0'>
                <Link to='/dashboard' className='d-lg-none'>
                  <img alt='Logo' src={toAbsoluteUrl('/favicon.ico')} className='h-30px' />
                </Link>
              </div>
            </div>
          </>
        )}

        {!(config.layoutType === 'dark-sidebar' || config.layoutType === 'light-sidebar') && (
          <div className='d-flex align-items-center flex-grow-1 flex-lg-grow-0 me-lg-15'>
            <Link to='/dashboard'>
              {config.layoutType !== 'dark-header' ? (
                <img
                  alt='Logo'
                  src={toAbsoluteUrl('/media/logos/default.svg')}
                  className='h-20px h-lg-30px app-sidebar-logo-default'
                />
              ) : (
                <>
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/logos/default-dark.svg')}
                    className='h-20px h-lg-30px app-sidebar-logo-default theme-light-show'
                  />
                  <img
                    alt='Logo'
                    src={toAbsoluteUrl('/media/logos/default-small-dark.svg')}
                    className='h-20px h-lg-30px app-sidebar-logo-default theme-dark-show'
                  />
                </>
              )}
            </Link>
          </div>
        )}

        <div
          id='kt_app_header_wrapper'
          className='d-flex align-items-stretch justify-content-between flex-lg-grow-1'
        >
          {config.app.header.default?.content === 'menu' &&
            config.app.header.default.menu?.display && (
              <div
                className='app-header-menu app-header-mobile-drawer align-items-stretch'
                data-kt-drawer='true'
                data-kt-drawer-name='app-header-menu'
                data-kt-drawer-activate='{default: true, lg: false}'
                data-kt-drawer-overlay='true'
                data-kt-drawer-width='225px'
                data-kt-drawer-direction='end'
                data-kt-drawer-toggle='#kt_app_header_menu_toggle'
                data-kt-swapper='true'
                data-kt-swapper-mode="{default: 'append', lg: 'prepend'}"
                data-kt-swapper-parent="{default: '#kt_app_body', lg: '#kt_app_header_wrapper'}"
              >
                <Header />
              </div>
            )}

          <div className='app-navbar flex-shrink-0'>
            <div
              className='app-navbar-item ms-1 ms-md-3'
              style={{position: 'relative'}}
              id='kt_header_user_menu_toggle'
            >
              <button
                className='btn btn-icon btn-custom btn-icon-muted btn-active-light btn-active-color-primary'
                onClick={handleNotificationBellClick}
                style={{position: 'relative'}}
              >
                <KTIcon iconName='notification-on' className='fs-2 fs-lg-1' />
                {unreadCount > 0 && !hasViewedNotifications && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '2px',
                      right: '2px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#dc2626',
                      borderRadius: '50%',
                      display: 'block',
                    }}
                  />
                )}
                {/* Unread count badge */}
                {unreadCount > 0 && !hasViewedNotifications && (
                  <span
                    style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65rem',
                      fontWeight: '700',
                      border: '2px solid white',
                    }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Modal */}
              {isNotificationOpen && (
                <>
                  {/* Backdrop to close menu when clicking outside */}
                  <div
                    className='position-fixed top-0 start-0 w-100 h-100'
                    style={{zIndex: 100}}
                    onClick={() => setIsNotificationOpen(false)}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50px',
                      right: 0,
                      width: '360px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                      zIndex: 1000,
                      maxHeight: '600px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Header */}
                    <div
                      style={{
                        padding: '1rem',
                        borderBottom: '1px solid #e5e7eb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#f9fafb',
                        borderTopLeftRadius: '0.5rem',
                        borderTopRightRadius: '0.5rem',
                      }}
                    >
                      <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                        <h3
                          style={{
                            margin: 0,
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            color: '#1f2937',
                          }}
                        >
                          Notifications
                        </h3>
                      </div>
                      <button
                        onClick={() => setIsNotificationOpen(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '1.5rem',
                          cursor: 'pointer',
                          color: '#6b7280',
                          padding: 0,
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'color 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#1f2937'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#6b7280'
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {/* Tab Navigation */}
                    <div
                      style={{
                        display: 'flex',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <button
                        onClick={() => setActiveNotificationTab('chat')}
                        style={{
                          flex: 1,
                          padding: '1rem',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: activeNotificationTab === 'chat' ? '#0891b2' : '#6b7280',
                          borderBottom:
                            activeNotificationTab === 'chat'
                              ? '3px solid #0891b2'
                              : '3px solid transparent',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (activeNotificationTab !== 'chat') {
                            e.currentTarget.style.color = '#374151'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeNotificationTab !== 'chat') {
                            e.currentTarget.style.color = '#6b7280'
                          }
                        }}
                      >
                        Unread Message
                        {headerChatNotifications.length > 0 && (
                          <span
                            style={{
                              marginLeft: '0.5rem',
                              backgroundColor: '#0891b2',
                              color: 'white',
                              borderRadius: '9999px',
                              padding: '0.125rem 0.5rem',
                              fontSize: '0.75rem',
                              fontWeight: '700',
                            }}
                          >
                            {headerChatNotifications.length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => setActiveNotificationTab('status')}
                        style={{
                          flex: 1,
                          padding: '1rem',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: activeNotificationTab === 'status' ? '#f16263' : '#6b7280',
                          borderBottom:
                            activeNotificationTab === 'status'
                              ? '3px solid #f16263'
                              : '3px solid transparent',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          if (activeNotificationTab !== 'status') {
                            e.currentTarget.style.color = '#374151'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (activeNotificationTab !== 'status') {
                            e.currentTarget.style.color = '#6b7280'
                          }
                        }}
                      >
                        Update Status
                        {headerStatusNotifications.length > 0 && (
                          <span
                            style={{
                              marginLeft: '0.5rem',
                              backgroundColor: '#f16263',
                              color: 'white',
                              borderRadius: '9999px',
                              padding: '0.125rem 0.5rem',
                              fontSize: '0.75rem',
                              fontWeight: '700',
                            }}
                          >
                            {headerStatusNotifications.length}
                          </span>
                        )}
                      </button>
                    </div>

                    {/* Content - WITH ORIGINAL STYLE */}
                    <div
                      className='tab-content'
                      style={{display: 'flex', flexDirection: 'column', flex: 1}}
                    >
                      <div
                        className='scroll-y mh-325px'
                        style={{padding: '0.5rem', flex: 1, overflow: 'auto'}}
                      >
                        {headerLoading ? (
                          <div className='d-flex flex-center py-5'>
                            <div
                              style={{
                                width: '24px',
                                height: '24px',
                                border: '3px solid #e5e7eb',
                                borderTop: '3px solid #127289',
                                borderRadius: '50%',
                                animation: 'spin 0.6s linear infinite',
                              }}
                            />
                          </div>
                        ) : activeNotificationTab === 'chat' ? (
                          // Chat Tab - Unread Message
                          headerChatNotifications.length === 0 ? (
                            <div className='d-flex flex-center text-gray-500 py-5'>
                              No unread messages
                            </div>
                          ) : (
                            headerChatNotifications.map((notification, index) => {
                              // Format datetime dengan timezone +7 (WIB)
                              const notificationDate = new Date(notification.createdAt)
                              // Add 7 hours for WIB timezone
                              const wibDate = new Date(
                                notificationDate.getTime() + 7 * 60 * 60 * 1000
                              )
                              const formattedDateTime = wibDate.toLocaleString('id-ID', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false,
                              })

                              return (
                                <div
                                  key={index}
                                  onClick={async () => {
                                    handleClaimAction(notification.claimNumber, false)
                                    setIsNotificationOpen(false)
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    padding: '1rem',
                                    marginBottom: '0.75rem',
                                    backgroundColor: '#e8f4f8',
                                    border: '1px solid #cce5f0',
                                    borderLeft: '4px solid #0891b2',
                                    borderRadius: '0.5rem',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    gap: '0.75rem',
                                    alignItems: 'flex-start',
                                    opacity: 1,
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.boxShadow =
                                      '0 4px 12px rgba(18, 114, 137, 0.15)'
                                    e.currentTarget.style.transform = 'translateX(2px)'
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.boxShadow = 'none'
                                    e.currentTarget.style.transform = 'translateX(0)'
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: '48px',
                                      height: '48px',
                                      borderRadius: '0.375rem',
                                      backgroundColor: '#0891b2',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <MessageSquareDot size={24} color='#ffffff' />
                                  </div>

                                  <div style={{flex: 1, minWidth: 0}}>
                                    <div
                                      style={{
                                        fontSize: '0.9rem',
                                        fontWeight: '700',
                                        color: '#1a202c',
                                        marginBottom: '0.35rem',
                                      }}
                                    >
                                      {notification.claimNumber}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '0.8rem',
                                        color: '#4b5563',
                                        marginBottom: '0.35rem',
                                        lineHeight: '1.4',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {notification.remark}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                      }}
                                    >
                                      <span>HealthMetrics</span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '0.7rem',
                                        color: '#9ca3af',
                                        marginTop: '0.35rem',
                                      }}
                                    >
                                      {formattedDateTime}
                                    </div>
                                  </div>
                                </div>
                              )
                            })
                          )
                        ) : // Status Tab - Update Status
                        headerStatusNotifications.length === 0 ? (
                          <div className='d-flex flex-center text-gray-500 py-5'>
                            No status updates
                          </div>
                        ) : (
                          headerStatusNotifications.map((notification, index) => {
                            // Format datetime dengan timezone +7 (WIB)
                            const notificationDate = new Date(notification.createdAt)
                            // Add 7 hours for WIB timezone
                            const wibDate = new Date(
                              notificationDate.getTime() + 7 * 60 * 60 * 1000
                            )
                            const formattedDateTime = wibDate.toLocaleString('id-ID', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false,
                            })

                            return (
                              <div
                                key={index}
                                onClick={async () => {
                                  handleClaimAction(notification.claimNumber, true)
                                  setIsNotificationOpen(false)
                                }}
                                style={{
                                  cursor: 'pointer',
                                  padding: '1rem',
                                  marginBottom: '0.75rem',
                                  backgroundColor: '#fef2f2',
                                  border: '1px solid #fccccb',
                                  borderLeft: '4px solid #f16263',
                                  borderRadius: '0.5rem',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  gap: '0.75rem',
                                  alignItems: 'flex-start',
                                  opacity: 1,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.boxShadow =
                                    '0 4px 12px rgba(18, 114, 137, 0.15)'
                                  e.currentTarget.style.transform = 'translateX(2px)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.boxShadow = 'none'
                                  e.currentTarget.style.transform = 'translateX(0)'
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '48px',
                                    height: '48px',
                                    borderRadius: '0.375rem',
                                    backgroundColor: '#f16263',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Megaphone size={24} color='#ffffff' />
                                </div>

                                <div style={{flex: 1, minWidth: 0}}>
                                  <div
                                    style={{
                                      fontSize: '0.9rem',
                                      fontWeight: '700',
                                      color: '#1a202c',
                                      marginBottom: '0.35rem',
                                    }}
                                  >
                                    {notification.claimNumber}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.8rem',
                                      color: '#4b5563',
                                      marginBottom: '0.35rem',
                                      lineHeight: '1.4',
                                    }}
                                  >
                                    {notification.remark}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.8rem',
                                      color: '#4b5563',
                                      marginBottom: '0.35rem',
                                      lineHeight: '1.4',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    <span>HealthMetrics</span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: '0.7rem',
                                      color: '#9ca3af',
                                      marginTop: '0.35rem',
                                    }}
                                  >
                                    {formattedDateTime}
                                  </div>
                                </div>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>

                    {/* Footer dengan action buttons */}
                    {(activeNotificationTab === 'chat'
                      ? headerChatNotifications.length > 0
                      : headerStatusNotifications.length > 0) && (
                      <div
                        style={{
                          padding: '0.75rem',
                          borderTop: '1px solid #e5e7eb',
                          backgroundColor: '#f9fafb',
                          display: 'flex',
                          gap: '0.5rem',
                          borderBottomLeftRadius: '0.5rem',
                          borderBottomRightRadius: '0.5rem',
                        }}
                      >
                        <button
                          onClick={() => {
                            setIsNotificationOpen(false)
                            navigate('/notifications')
                          }}
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            backgroundColor: '#127289',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#0e5568'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#127289'
                          }}
                        >
                          View All
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <Navbar />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
